import { useEffect } from 'react'
import { Dispatch } from 'redux'
import { FormikProps } from 'formik'
import { ObjectSchema } from 'yup'
import { useSelector, useDispatch } from 'react-redux'

import { ApplicationState } from 'src/core/store'
import { Currency, Id, PaymentResource } from 'src/types'
import {
  getResources,
  reloadResourcesPage,
  setResources
} from 'src/core/store/ducks/resources/actions'
import { PAGE_SIZE_EDIT } from 'src/core/store/ducks/resources'
import { ResourcesAvailable } from 'src/core/store/ducks/resources/types'
import isThisType from 'src/helpers/is-this-ype'

// import useBudgetItemsValues from './BudgetItems'
import * as BillingTypes from './billing-types'
import * as Contracts from './contracts'
import * as CostCenters from './cost-centers'
import * as Currencies from './currencies'
import * as FinancialResources from './financial-resources'
import * as PaymentResources from './payment-resources'
import * as Reasons from './reasons'
import * as References from './references'
import * as Services from './services'
import * as Targets from './targets'

import {
  ResourcesCrudDataColumnsType,
  ResourcesCrudOnSubmitReturn,
  ResourcesCrudProps
} from '../../components/resources-crud'

type KeyResource = keyof ResourcesAvailable
type ValueResource = ResourcesAvailable[KeyResource]

export const validKeyResources: Array<keyof ResourcesAvailable> = [
  'billing_types',
  // 'budget_items',
  'contracts',
  'cost_centers',
  'currencies',
  'financial_resources',
  'payment_resources',
  'reasons',
  'references',
  'services',
  'targets'
]

function isValidResource(resource: string): boolean {
  return validKeyResources.includes(resource as KeyResource)
}

function getHeader(resource: KeyResource): JSX.Element {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getHeader
    case 'contracts':
      return Contracts.getHeader
    case 'cost_centers':
      return CostCenters.getHeader
    case 'currencies':
      return Currencies.getHeader
    case 'financial_resources':
      return FinancialResources.getHeader
    case 'payment_resources':
      return PaymentResources.getHeader
    case 'reasons':
      return Reasons.getHeader
    case 'references':
      return References.getHeader
    case 'services':
      return Services.getHeader
    case 'targets':
      return Targets.getHeader
    default:
      return null
  }
}

function getDataColumns(resource: KeyResource): ResourcesCrudDataColumnsType {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getDataColumns
    case 'contracts':
      return Contracts.getDataColumns
    case 'cost_centers':
      return CostCenters.getDataColumns
    case 'currencies':
      return Currencies.getDataColumns
    case 'financial_resources':
      return FinancialResources.getDataColumns
    case 'payment_resources':
      return PaymentResources.getDataColumns
    case 'reasons':
      return Reasons.getDataColumns
    case 'references':
      return References.getDataColumns
    case 'services':
      return Services.getDataColumns
    case 'targets':
      return Targets.getDataColumns
    default:
      return null
  }
}

function getPage(
  resource: KeyResource,
  dispatch: Dispatch<any>
): { (page: number): void } {
  return (page: number) => {
    dispatch(getResources(resource, { page }, true))
  }
}

function getInitialValues(
  resource: KeyResource,
  organization_id: Id
): ValueResource {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getInitialValues(organization_id)
    case 'contracts':
      return Contracts.getInitialValues(organization_id)
    case 'cost_centers':
      return CostCenters.getInitialValues(organization_id)
    case 'currencies':
      return Currencies.getInitialValues()
    case 'financial_resources':
      return FinancialResources.getInitialValues(organization_id)
    case 'payment_resources':
      return PaymentResources.getInitialValues(organization_id)
    case 'reasons':
      return Reasons.getInitialValues(organization_id)
    case 'references':
      return References.getInitialValues(organization_id)
    case 'services':
      return Services.getInitialValues(organization_id)
    case 'targets':
      return Targets.getInitialValues(organization_id)
    default:
      return null
  }
}

interface GetOnSubmitProps {
  dispatch: Dispatch<any>
  reloadResourcesPageFn: typeof reloadResourcesPage
  resource: KeyResource
  setResourcesFn: typeof setResources
}

function getOnSubmit({
  dispatch,
  reloadResourcesPageFn,
  resource,
  setResourcesFn
}: GetOnSubmitProps): ResourcesCrudOnSubmitReturn {
  return async function submit(values: ValueResource) {
    if (values) {
      const sanitized = Object.assign({}, values)
      // @ts-ignore
      delete sanitized.key

      if (!isThisType<Currency>(sanitized, 'currency_id')) {
        sanitized.organization_id = +sanitized.organization_id
        delete sanitized.organization
      }

      if (isThisType<PaymentResource>(sanitized, 'payment_resource_id')) {
        if (sanitized.toodopay) {
          sanitized.locked = true
        }
      }

      await dispatch(setResourcesFn(resource, [sanitized]))
      // if it is a new standard, it must reload standard of the old record
      // if it is inactivated the record should disappear
      if (sanitized.standard || !sanitized.active) {
        await dispatch(reloadResourcesPageFn(resource))
      }
    }
  }
}

function getDrawer(
  resource: KeyResource,
  isSuperAdmin: boolean
): (formikProps: FormikProps<any>) => JSX.Element {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getDrawer(isSuperAdmin)
    case 'contracts':
      return Contracts.getDrawer(isSuperAdmin)
    case 'cost_centers':
      return CostCenters.getDrawer(isSuperAdmin)
    case 'currencies':
      return Currencies.getDrawer()
    case 'financial_resources':
      return FinancialResources.getDrawer(isSuperAdmin)
    case 'payment_resources':
      return PaymentResources.getDrawer(isSuperAdmin)
    case 'reasons':
      return Reasons.getDrawer(isSuperAdmin)
    case 'references':
      return References.getDrawer(isSuperAdmin)
    case 'services':
      return Services.getDrawer(isSuperAdmin)
    case 'targets':
      return Targets.getDrawer(isSuperAdmin)
    default:
      return null
  }
}

function getTitle(resource: KeyResource): string {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getTitle
    case 'contracts':
      return Contracts.getTitle
    case 'cost_centers':
      return CostCenters.getTitle
    case 'currencies':
      return Currencies.getTitle
    case 'financial_resources':
      return FinancialResources.getTitle
    case 'payment_resources':
      return PaymentResources.getTitle
    case 'reasons':
      return Reasons.getTitle
    case 'references':
      return References.getTitle
    case 'services':
      return Services.getTitle
    case 'targets':
      return Targets.getTitle
    default:
      return null
  }
}

function getValidationSchema(resource: KeyResource): () => ObjectSchema<any> {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getValidationSchema
    case 'contracts':
      return Contracts.getValidationSchema
    case 'cost_centers':
      return CostCenters.getValidationSchema
    case 'currencies':
      return Currencies.getValidationSchema
    case 'financial_resources':
      return FinancialResources.getValidationSchema
    case 'payment_resources':
      return PaymentResources.getValidationSchema
    case 'reasons':
      return Reasons.getValidationSchema
    case 'references':
      return References.getValidationSchema
    case 'services':
      return Services.getValidationSchema
    case 'targets':
      return Targets.getValidationSchema
    default:
      return null
  }
}

function getWidth(resource: KeyResource): string {
  switch (resource) {
    case 'billing_types':
      return BillingTypes.getDrawerWidth
    case 'contracts':
      return Contracts.getDrawerWidth
    case 'cost_centers':
      return CostCenters.getDrawerWidth
    case 'currencies':
      return Currencies.getDrawerWidth
    case 'financial_resources':
      return FinancialResources.getDrawerWidth
    case 'payment_resources':
      return PaymentResources.getDrawerWidth
    case 'reasons':
      return Reasons.getDrawerWidth
    case 'references':
      return References.getDrawerWidth
    case 'services':
      return Services.getDrawerWidth
    case 'targets':
      return Targets.getDrawerWidth
    default:
      return null
  }
}

interface UseGetResourcesFormItemsProps {
  resource: string
}

export default function useGetResourcesFormItems({
  resource = ''
}: UseGetResourcesFormItemsProps): ResourcesCrudProps {
  const {
    me: {
      user: { organization_id, user_level_type }
    },
    resources
  } = useSelector((state: ApplicationState) => ({
    me: state.me,
    resources: state.resources
  }))

  const dispatch = useDispatch()

  const keyResource = resource as KeyResource
  const isSuperAdmin = ['SUPER_ADMIN', 'REALM_ADMIN'].includes(user_level_type)

  useEffect(() => {
    if (isValidResource(resource)) {
      dispatch(getResources(resource as KeyResource, { page: 1 }, true))
    }
  }, [dispatch, keyResource, resource])

  if (!isValidResource) {
    return null
  }

  const pagination = {
    current: resources[keyResource].page,
    pageSize: PAGE_SIZE_EDIT,
    total: resources[keyResource].total_results
  }

  return {
    dataColumns: getDataColumns(keyResource) as any,
    dataSource: resources[keyResource].list,
    drawerForm: getDrawer(keyResource, isSuperAdmin),
    getPage: getPage(keyResource, dispatch),
    header: getHeader(keyResource),
    initialValues: getInitialValues(keyResource, organization_id),
    isLoading: resources[keyResource].loading,
    onSubmit: getOnSubmit({
      dispatch,
      reloadResourcesPageFn: reloadResourcesPage,
      resource: keyResource,
      setResourcesFn: setResources
    }),
    pagination,
    title: getTitle(keyResource),
    validationSchema: getValidationSchema(keyResource),
    width: getWidth(keyResource)
  }
}
